import React from 'react';
import clsx from 'clsx';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { Combobox } from '@oysterjs/ui/combobox';
import { Input } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';

import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  AmsAccountMatchReason,
  AmsAccountSyncScope,
  AmsReconciliationStartingState,
  type AmsAccountMatchFieldsFragment,
  type AmsDetectedEntitiesFieldsFragment,
  type AmsReconciliationSourceInput,
  type AmsReconciliationTargetFieldsFragment,
  useAmsAgencyStructureQuery,
  useAmsReconciliationTargetsQuery,
  useCreateAmsAccountMutation,
  useImportAmsAccountByExternalIdMutation,
  usePrepareAmsReconciliationMutation,
  useRequestAmsAccountRefreshMutation,
  useSearchAmsAccountsLiveQuery,
  useSearchAmsAccountsQuery
} from '@oysterjs/ais/api/graphql';
import { useDebouncedValue } from '@oysterjs/ais/hooks/use-list-controls';
import { MATCH_REASON_LABEL } from '@oysterjs/ais/lib/ams-match-reason';
import { type DestinationPolicy, inPickerOrder, layOutDestinations } from './destinations';
import { InlineEditValue } from './inline-edit';
import { StepOneSkeleton } from './step-one-skeleton';
import type { ReconcilableTarget } from './pipeline';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULT_LIMIT = 20;
const SYNC_POLL_INTERVAL_MS = 4_000;

/** The account a run will write to: one that already exists, or one to create. */
export type StepOneAccount =
  | { kind: 'existing'; match: AmsAccountMatchFieldsFragment }
  | ({ kind: 'new' } & NewAccountDraft);

export type StepOneSelection = {
  account: StepOneAccount;
  /** Full targets, not just their line of business: a run needs each one's policyId. */
  destinations: ReconcilableTarget[];
};

export type AmsReconciliationStepOneProps = {
  source: AmsReconciliationSourceInput;
  workspaceId?: string;
  onCancel: () => void;
  onContinue: (selection: StepOneSelection) => void;
  onAccountCreated: (accountName: string) => void;
};

/**
 * Step one: settle which AMS account a document belongs to, and which of that
 * account's records a run may write to. One picker serves every starting state, and
 * prepareAmsReconciliation decides which one it opens in.
 */
export function AmsReconciliationStepOne(props: AmsReconciliationStepOneProps) {
  const [prepare, prepared] = usePrepareAmsReconciliationMutation();
  // Each call runs a billed detection, so it fires once per document, StrictMode's
  // repeated effect included.
  const preparedFor = React.useRef<string | null>(null);
  const prepareKey = JSON.stringify({ source: props.source, workspaceId: props.workspaceId });
  React.useEffect(() => {
    if (preparedFor.current === prepareKey) {
      return;
    }
    preparedFor.current = prepareKey;
    // A failure surfaces through prepared.error.
    prepare({ variables: { input: JSON.parse(prepareKey) } }).catch(() => undefined);
  }, [prepare, prepareKey]);
  const { data, error } = prepared;
  const loading = prepared.loading || !prepared.called;
  const prep = data?.prepareAmsReconciliation;

  const [chosen, setChosen] = React.useState<AmsAccountMatchFieldsFragment | null>(null);
  const [creatingNew, setCreatingNew] = React.useState(false);
  const [picking, setPicking] = React.useState(false);
  const [newAccount, setNewAccount] = React.useState<NewAccountDraft>({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    agencyCode: '',
    branchCode: ''
  });
  const [destinations, setDestinations] = React.useState<string[]>([]);

  // A live match has no local row, and so no destinations, until it is imported.
  // Choosing one imports it, so the id below comes from the match or the import.
  const [importedAccountIds, setImportedAccountIds] = React.useState<Record<string, string>>({});
  const [importSyncing, setImportSyncing] = React.useState(false);
  const [importAccount, { loading: importing }] = useImportAmsAccountByExternalIdMutation();

  const [createAccount, { loading: creatingAccount }] = useCreateAmsAccountMutation();
  const [createAccountError, setCreateAccountError] = React.useState<string | null>(null);

  const chosenAccountId = chosen
    ? (chosen.accountId ?? importedAccountIds[accountKey(chosen)] ?? null)
    : null;

  const ensureImported = React.useCallback(
    async (match: AmsAccountMatchFieldsFragment) => {
      if (match.accountId || importedAccountIds[accountKey(match)]) {
        return;
      }
      const result = await importAccount({
        variables: {
          input: {
            integrationId: match.integrationId,
            externalId: match.externalId,
            displayName: match.displayName
          }
        }
      });
      const imported = result.data?.importAmsAccountByExternalId;
      if (imported) {
        setImportedAccountIds((current) => ({
          ...current,
          [accountKey(match)]: imported.accountId
        }));
        setImportSyncing(imported.syncing);
      }
    },
    [importAccount, importedAccountIds]
  );

  const accountId = creatingNew ? null : chosenAccountId;

  // Destinations belong to one account; carrying ticks across a change of account
  // would apply the run to a policy nobody chose.
  React.useEffect(() => {
    setDestinations([]);
  }, [accountId]);

  const targetsQuery = useAmsReconciliationTargetsQuery({
    variables: {
      input: {
        accountId: accountId ?? '',
        detected: {
          accountName: prep?.detected.accountName,
          accountEmail: prep?.detected.accountEmail,
          accountPhone: prep?.detected.accountPhone,
          policyNumber: prep?.detected.policyNumber,
          generalLiability: prep?.detected.generalLiability ?? false,
          businessAuto: prep?.detected.businessAuto ?? false,
          commercialProperty: prep?.detected.commercialProperty ?? false,
          workersCompensation: prep?.detected.workersCompensation ?? false,
          personalAuto: prep?.detected.personalAuto ?? false,
          homeowner: prep?.detected.homeowner ?? false,
          personalUmbrella: prep?.detected.personalUmbrella ?? false,
          applicant: prep?.detected.applicant ?? false
        }
      }
    },
    skip: !prep || !accountId
  });

  const [requestRefresh, { loading: requestingRefresh }] = useRequestAmsAccountRefreshMutation();
  const [syncedAt, setSyncedAt] = React.useState<Date | null>(null);
  const { refetch: refetchTargets } = targetsQuery;

  // Destinations scope only: reconciliation reads everything else live. An enqueued
  // refresh shows only through the targets query's syncing flag, so refetching is
  // what starts the poll below.
  const refreshAccount = React.useCallback(
    async (id: string, force: boolean) => {
      try {
        const result = await requestRefresh({
          variables: { accountId: id, force, scope: AmsAccountSyncScope.Destinations }
        });
        const refresh = result.data?.requestAmsAccountRefresh;
        if (!refresh) {
          return;
        }
        setSyncedAt(new Date(refresh.syncedAt));
        if (refresh.enqueued) {
          void refetchTargets();
        }
      } catch {
        // Listed destinations stay usable, as current as the last sync.
      }
    },
    [requestRefresh, refetchTargets]
  );

  // An account Cara already held can be hours behind Epic. One imported this session
  // is skipped, since the import started a refresh. The server's staleness gate
  // decides whether anything runs.
  const alreadyHeld = chosen?.accountId != null && chosen.accountId === accountId;
  React.useEffect(() => {
    setSyncedAt(null);
    if (accountId && alreadyHeld) {
      void refreshAccount(accountId, false);
    }
    // Keyed on the account alone: refreshAccount changes identity with the targets
    // query, and re-running would re-request on every render.
  }, [accountId]);

  const targets = targetsQuery.data?.amsReconciliationTargets.targets ?? [];
  // The import's report covers the gap until the first targets query returns.
  const syncing = targetsQuery.data?.amsReconciliationTargets.syncing ?? importSyncing;

  // The mutation reports the sync time a refresh started from; once one finishes the
  // data is as of now, to within one poll.
  const wasSyncing = React.useRef(false);
  React.useEffect(() => {
    if (wasSyncing.current && !syncing) {
      setSyncedAt(new Date());
    }
    wasSyncing.current = syncing;
  }, [syncing]);

  // A just-imported account has no policies until its refresh lands, so poll rather
  // than let an empty list read as the account genuinely having none.
  const { startPolling, stopPolling } = targetsQuery;
  React.useEffect(() => {
    if (syncing) {
      startPolling(SYNC_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [syncing, startPolling, stopPolling]);

  // The prefilled account and the create-account form both start from what the
  // document said, so they can only be seeded once the detection pass has returned.
  React.useEffect(() => {
    if (!prep) {
      return;
    }
    const seeded = prep.workspaceClientAccount ?? prep.suggestedAccounts[0] ?? null;
    setChosen(seeded);
    setCreatingNew(prep.startingState === AmsReconciliationStartingState.CreateNew);
    if (seeded) {
      void ensureImported(seeded);
    }
    setNewAccount({
      name: prep.detected.accountName ?? '',
      firstName: prep.detected.contactFirstName ?? '',
      lastName: prep.detected.contactLastName ?? '',
      email: prep.detected.accountEmail ?? '',
      phone: prep.detected.accountPhone ?? '',
      agencyCode: '',
      branchCode: ''
    });
    // Keyed on prep alone: ensureImported changes identity once an import lands, and
    // re-running this then would throw away whatever the user has since picked.
  }, [prep]);

  if (loading) {
    return <StepOneSkeleton onCancel={props.onCancel} />;
  }

  if (error || !prep) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
        Couldn't read this document to find its AMS account. Try again.
      </div>
    );
  }

  const account: StepOneAccount | null = creatingNew
    ? { kind: 'new', ...newAccount }
    : chosen
      ? { kind: 'existing', match: chosen }
      : null;

  return (
    <div className="flex flex-col gap-6">
      <AccountSection
        prep={prep}
        chosen={chosen}
        creatingNew={creatingNew}
        picking={picking}
        newAccount={newAccount}
        onPick={(match) => {
          setChosen(match);
          setCreatingNew(false);
          setPicking(false);
          void ensureImported(match);
        }}
        onStartPicking={() => setPicking(true)}
        onCreateNew={() => {
          setCreatingNew(true);
          setChosen(null);
        }}
        onNewAccountChange={setNewAccount}
      />

      <DestinationSection
        accountId={accountId}
        hasChosenAccount={chosen != null}
        // An account being created has nothing in the AMS, so list what the document asks for.
        targets={creatingNew ? prep.newAccountTargets : targets}
        loading={!creatingNew && targetsQuery.loading}
        syncing={!creatingNew && (syncing || importing)}
        creatingNew={creatingNew}
        syncedAt={syncedAt}
        refreshing={requestingRefresh}
        onRefresh={accountId ? () => void refreshAccount(accountId, true) : undefined}
        selected={destinations}
        onToggle={(targetId) =>
          setDestinations((current) =>
            current.includes(targetId)
              ? current.filter((entry) => entry !== targetId)
              : [...current, targetId]
          )
        }
      />

      {createAccountError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {createAccountError}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" color="white" onClick={props.onCancel}>
          Cancel
        </Button>
        {creatingNew ? (
          <Button
            type="button"
            color="sky"
            disabled={
              !newAccount.name.trim() ||
              !newAccount.lastName.trim() ||
              !newAccount.agencyCode ||
              !newAccount.branchCode ||
              !prep.integrationId ||
              creatingAccount
            }
            onClick={async () => {
              if (!prep.integrationId) {
                return;
              }
              setCreateAccountError(null);
              try {
                const result = await createAccount({
                  variables: {
                    input: {
                      integrationId: prep.integrationId,
                      name: newAccount.name.trim(),
                      agencyCode: newAccount.agencyCode,
                      branchCode: newAccount.branchCode,
                      firstName: newAccount.firstName.trim() || undefined,
                      lastName: newAccount.lastName.trim(),
                      email: newAccount.email.trim() || undefined,
                      phone: newAccount.phone.trim() || undefined
                    }
                  }
                });
                const failure = messageOfFailure(
                  result,
                  "The AMS wouldn't accept this account. Nothing was created."
                );
                if (failure || !result.data) {
                  setCreateAccountError(
                    failure ?? "The AMS wouldn't accept this account. Nothing was created."
                  );
                  return;
                }
                props.onAccountCreated(
                  result.data.createAmsAccount.displayName ?? newAccount.name.trim()
                );
              } catch {
                setCreateAccountError("Couldn't reach the AMS to create this account.");
              }
            }}
          >
            {creatingAccount ? 'Creating…' : 'Create account'}
          </Button>
        ) : (
          <Button
            type="button"
            color="sky"
            disabled={!account || destinations.length === 0}
            onClick={() =>
              account &&
              props.onContinue({
                account,
                destinations: inPickerOrder(targets).filter(
                  (target): target is ReconcilableTarget =>
                    destinations.includes(target.id) && target.lineOfBusiness != null
                )
              })
            }
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}

function AccountSection(props: {
  prep: {
    startingState: AmsReconciliationStartingState;
    detected: AmsDetectedEntitiesFieldsFragment;
    workspaceClientAccount?: AmsAccountMatchFieldsFragment | null;
    suggestedAccounts: AmsAccountMatchFieldsFragment[];
    integrationId?: string | null;
  };
  chosen: AmsAccountMatchFieldsFragment | null;
  creatingNew: boolean;
  picking: boolean;
  newAccount: NewAccountDraft;
  onPick: (match: AmsAccountMatchFieldsFragment) => void;
  onStartPicking: () => void;
  onCreateNew: () => void;
  onNewAccountChange: (next: NewAccountDraft) => void;
}) {
  const { prep } = props;

  // The prefilled state collapses to a single row until asked to expand: a linked
  // client is already the answer, so the matches only matter if it is wrong.
  const prefilledOnly =
    prep.startingState === AmsReconciliationStartingState.WorkspaceClient &&
    !props.picking &&
    !props.creatingNew &&
    props.chosen != null;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
          AMS account
        </h3>
        {prefilledOnly && (
          <button
            type="button"
            onClick={props.onStartPicking}
            className="text-xs font-medium text-sky-700 hover:underline dark:text-sky-300"
          >
            Change
          </button>
        )}
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {accountSectionCaption(prep, props.creatingNew, prefilledOnly)}
      </p>

      {prefilledOnly && props.chosen ? (
        <AccountRow match={props.chosen} selected onSelect={() => undefined} />
      ) : (
        <AccountPicker
          prep={prep}
          chosen={props.chosen}
          creatingNew={props.creatingNew}
          newAccount={props.newAccount}
          onPick={props.onPick}
          onCreateNew={props.onCreateNew}
          onNewAccountChange={props.onNewAccountChange}
        />
      )}
    </section>
  );
}

function accountSectionCaption(
  prep: {
    startingState: AmsReconciliationStartingState;
    suggestedAccounts: AmsAccountMatchFieldsFragment[];
  },
  creatingNew: boolean,
  prefilledOnly: boolean
): string {
  if (creatingNew) {
    return 'No account selected. This run will create one from the document.';
  }
  if (prefilledOnly) {
    return 'Prefilled from the AMS account this client is already linked to.';
  }
  if (prep.suggestedAccounts.length === 0) {
    return 'Nothing in the AMS matched this document. Search by name, email or phone.';
  }
  return `${prep.suggestedAccounts.length} possible ${
    prep.suggestedAccounts.length === 1 ? 'match' : 'matches'
  } from this document. Search to look further.`;
}

function AccountPicker(props: {
  prep: {
    workspaceClientAccount?: AmsAccountMatchFieldsFragment | null;
    suggestedAccounts: AmsAccountMatchFieldsFragment[];
    integrationId?: string | null;
  };
  chosen: AmsAccountMatchFieldsFragment | null;
  creatingNew: boolean;
  newAccount: NewAccountDraft;
  onPick: (match: AmsAccountMatchFieldsFragment) => void;
  onCreateNew: () => void;
  onNewAccountChange: (next: NewAccountDraft) => void;
}) {
  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);

  // Typed search may read a stale knowledge graph, unlike suggestedAccounts, which
  // matches the AMS live: a stale typed result is in front of someone who can see it
  // is wrong, while a stale automatic match would silently pick the wrong account.
  const { data: searchData, loading: searching } = useSearchAmsAccountsQuery({
    variables: { query: debouncedQuery, limit: SEARCH_RESULT_LIMIT },
    skip: debouncedQuery.length === 0
  });

  // Epic is searched in parallel: the graph is fast but only knows synced accounts.
  const { data: liveData, loading: liveSearching } = useSearchAmsAccountsLiveQuery({
    variables: { input: { query: debouncedQuery, limit: SEARCH_RESULT_LIMIT } },
    skip: debouncedQuery.length === 0
  });

  const knownResults: AmsAccountMatchFieldsFragment[] = (
    searchData?.searchAmsAccounts.entities ?? []
  ).map((entity) => ({
    __typename: 'AmsAccountMatch',
    integrationId: entity.integration.id,
    externalId: entity.externalId,
    accountId: entity.id,
    displayName: entity.displayName,
    email: entity.searchMetadata.account?.email ?? null,
    phone: entity.searchMetadata.account?.phone ?? null,
    lookupCode: entity.searchMetadata.account?.lookupCode ?? null,
    accountCode: entity.searchMetadata.account?.lookupCode ?? null,
    reason: AmsAccountMatchReason.SimilarName
  }));
  const known = new Set(knownResults.map(accountKey));
  const searchResults = [
    ...knownResults,
    ...(liveData?.searchAmsAccountsLive ?? []).filter((match) => !known.has(accountKey(match)))
  ];

  return (
    <div className="flex flex-col gap-2">
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by name, email, phone, lookup code or policy number"
        aria-label="Search AMS accounts"
      />

      {props.prep.workspaceClientAccount && (
        <AccountGroup label="Current">
          <AccountRow
            match={props.prep.workspaceClientAccount}
            selected={
              !props.creatingNew && sameAccount(props.chosen, props.prep.workspaceClientAccount)
            }
            onSelect={() => props.onPick(props.prep.workspaceClientAccount!)}
          />
        </AccountGroup>
      )}

      {props.prep.suggestedAccounts.length > 0 && (
        <AccountGroup label="Suggested from this document">
          {props.prep.suggestedAccounts.map((match) => (
            <AccountRow
              key={accountKey(match)}
              match={match}
              selected={!props.creatingNew && sameAccount(props.chosen, match)}
              onSelect={() => props.onPick(match)}
            />
          ))}
        </AccountGroup>
      )}

      {debouncedQuery.length > 0 && (
        <AccountGroup label="Search results">
          {searchResults.map((match) => (
            <AccountRow
              key={accountKey(match)}
              match={match}
              hideReason
              selected={!props.creatingNew && sameAccount(props.chosen, match)}
              onSelect={() => props.onPick(match)}
            />
          ))}
          {searching || liveSearching ? (
            <div className="flex items-center gap-2 px-3.5 py-3 text-xs text-neutral-500 dark:text-neutral-400">
              <Spinner color="neutral" />
              {searching ? 'Searching…' : 'Searching Applied Epic…'}
            </div>
          ) : (
            searchResults.length === 0 && (
              <div className="px-3.5 py-3 text-xs text-neutral-500 dark:text-neutral-400">
                No accounts match “{debouncedQuery}”.
              </div>
            )
          )}
        </AccountGroup>
      )}

      {props.creatingNew ? (
        // Once chosen, the option becomes a card holding the new account's details.
        <div className="rounded-lg border border-sky-500 px-3.5 py-3 ring-2 ring-sky-500/20">
          <div className="flex items-center gap-3">
            <RadioDot checked />
            <div className="min-w-0 text-sm font-medium text-neutral-950 dark:text-white">
              None of these, create a new account
            </div>
            <Badge color="sky" className="ml-auto">
              New
            </Badge>
          </div>
          <NewAccountForm value={props.newAccount} onChange={props.onNewAccountChange} />
          {props.prep.integrationId && (
            <NewAccountStructureFields
              integrationId={props.prep.integrationId}
              value={props.newAccount}
              onChange={props.onNewAccountChange}
            />
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={props.onCreateNew}
          role="radio"
          aria-checked={false}
          className="flex items-start gap-3 rounded-lg border border-dashed border-neutral-950/15 px-3.5 py-3 text-left hover:bg-neutral-50 dark:border-white/15 dark:hover:bg-white/5"
        >
          <RadioDot checked={false} />
          <div className="min-w-0">
            <div className="text-sm text-neutral-950 dark:text-white">
              None of these, create a new account
            </div>
            <div className="mt-0.5 text-[13px]/[18px] text-neutral-500 dark:text-neutral-400">
              Cara creates it from this document when you apply
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

function AccountGroup(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
        {props.label}
      </div>
      <div
        role="radiogroup"
        aria-label={props.label}
        className="divide-y divide-neutral-950/5 overflow-hidden rounded-lg border border-neutral-950/10 dark:divide-white/5 dark:border-white/10"
      >
        {props.children}
      </div>
    </div>
  );
}

function AccountRow(props: {
  match: AmsAccountMatchFieldsFragment;
  selected: boolean;
  hideReason?: boolean;
  onSelect: () => void;
}) {
  const { match } = props;
  const accountCode = accountCodeOf(match);
  const detail = [accountCode && `Account ${accountCode}`, match.email, match.phone]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      onClick={props.onSelect}
      className="flex w-full items-start gap-3 px-3.5 py-3 text-left hover:bg-neutral-50 dark:hover:bg-white/5"
    >
      <RadioDot checked={props.selected} />
      <div className="min-w-0">
        <div
          className={clsx(
            'truncate text-sm text-neutral-950 dark:text-white',
            props.selected && 'font-medium'
          )}
        >
          {match.displayName || 'Unnamed account'}
        </div>
        <div className="mt-0.5 truncate text-[13px]/[18px] text-neutral-500 dark:text-neutral-400">
          {detail || 'No contact on file'}
        </div>
      </div>
      {!props.hideReason && (
        // The details truncate instead: a wrapped reason reads as two labels.
        <Badge color="zinc" className="ml-auto shrink-0 whitespace-nowrap">
          {MATCH_REASON_LABEL[match.reason]}
        </Badge>
      )}
    </button>
  );
}

/** The account options' radio, drawn rather than native so a whole row is the target. */
function RadioDot(props: { checked: boolean }) {
  return props.checked ? (
    <span
      aria-hidden="true"
      className="mt-px grid size-[17px] flex-none place-items-center rounded-full bg-sky-500 shadow-sm"
    >
      <span
        // dark-mode-exempt: the dot stays white in both themes; the sky fill
        // behind it carries the selection.
        className="size-2 rounded-full bg-white"
      />
    </span>
  ) : (
    <span
      aria-hidden="true"
      className="mt-px size-[17px] flex-none rounded-full border border-neutral-950/15 bg-white shadow-sm dark:border-white/15 dark:bg-white/5"
    />
  );
}

type NewAccountDraft = {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  agencyCode: string;
  branchCode: string;
};

const NEW_ACCOUNT_FIELDS: {
  key: Exclude<keyof NewAccountDraft, 'agencyCode' | 'branchCode'>;
  label: string;
  hint?: string;
}[] = [
  { key: 'name', label: 'Business name / Account name' },
  { key: 'firstName', label: 'Primary contact first name' },
  {
    key: 'lastName',
    label: 'Primary contact last name',
    hint: "Applied Epic needs the primary contact's last name to create the account."
  },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' }
];

function NewAccountForm(props: {
  value: NewAccountDraft;
  onChange: (next: NewAccountDraft) => void;
}) {
  return (
    <div className="mt-3 flex flex-col gap-2 pl-[29px]">
      {NEW_ACCOUNT_FIELDS.map(({ key, label, hint }) => (
        <NewAccountField
          key={key}
          label={label}
          hint={hint}
          value={props.value[key]}
          onChange={(next) => props.onChange({ ...props.value, [key]: next })}
        />
      ))}
    </div>
  );
}

/** One detail read from the document, edited in place from its pencil. */
function NewAccountField(props: {
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const [editing, setEditing] = React.useState(false);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5">
        <span className="w-[168px] flex-none text-[13px]/5 text-neutral-500 dark:text-neutral-400">
          {props.label}
        </span>
        <InlineEditValue
          label={props.label}
          value={props.value}
          placeholder="Not found in document"
          editing={editing}
          editable
          onStartEditing={() => setEditing(true)}
          onChange={props.onChange}
          onDoneEditing={() => setEditing(false)}
        />
      </div>
      {props.hint && (
        <p className="pl-[178px] text-xs text-neutral-500 dark:text-neutral-400">{props.hint}</p>
      )}
    </div>
  );
}

/**
 * Where in the AMS's agency structure the new account is filed. Epic refuses a client
 * without an agency and branch, so creating waits on both. A list with one entry is
 * chosen for the user and shown as text.
 */
function NewAccountStructureFields(props: {
  integrationId: string;
  value: NewAccountDraft;
  onChange: (next: NewAccountDraft) => void;
}) {
  const { data, loading, error } = useAmsAgencyStructureQuery({
    variables: { integrationId: props.integrationId }
  });
  const agencies = React.useMemo(() => data?.amsAgencyStructure ?? [], [data]);
  const agency = agencies.find((entry) => entry.code === props.value.agencyCode);

  const chooseAgency = (agencyCode: string) => {
    const branches = agencies.find((entry) => entry.code === agencyCode)?.branches ?? [];
    props.onChange({
      ...props.value,
      agencyCode,
      branchCode: branches.length === 1 ? branches[0].code : ''
    });
  };

  // Keyed on the list alone: re-running on every draft edit would undo a cleared pick.
  React.useEffect(() => {
    if (agencies.length === 1 && !props.value.agencyCode) {
      chooseAgency(agencies[0].code);
    }
  }, [agencies]);

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2 pl-[29px] text-xs text-neutral-500 dark:text-neutral-400">
        <Spinner color="neutral" />
        Loading agencies from the AMS…
      </div>
    );
  }
  if (error || agencies.length === 0) {
    return (
      <div className="mt-2 pl-[29px] text-xs text-red-700 dark:text-red-300">
        {error
          ? "Couldn't load agencies and branches from the AMS."
          : 'The AMS lists no agency with a branch to file this account under.'}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-2 pl-[29px]">
      <StructureField
        label="Agency"
        placeholder="Choose an agency"
        options={agencies}
        value={props.value.agencyCode}
        onChange={chooseAgency}
      />
      <StructureField
        label="Branch"
        placeholder={agency ? 'Choose a branch' : 'Choose an agency first'}
        options={agency?.branches ?? []}
        value={props.value.branchCode}
        disabled={!agency}
        onChange={(branchCode) => props.onChange({ ...props.value, branchCode })}
      />
    </div>
  );
}

function StructureField(props: {
  label: string;
  placeholder: string;
  options: readonly { code: string; label: string }[];
  value: string;
  disabled?: boolean;
  onChange: (code: string) => void;
}) {
  const only = props.options.length === 1 ? props.options[0] : null;

  return (
    <div className="flex items-center gap-2.5">
      <span className="w-[168px] flex-none text-[13px]/5 text-neutral-500 dark:text-neutral-400">
        {props.label}
      </span>
      {only ? (
        <span className="min-w-0 truncate text-sm font-medium text-neutral-800 dark:text-neutral-200">
          {only.label}
        </span>
      ) : (
        <div className="min-w-0 flex-1">
          <Combobox
            value={props.value || undefined}
            options={props.options.map((option) => ({
              value: option.code,
              label: option.label,
              description: option.code
            }))}
            placeholder={props.placeholder}
            minQueryLength={0}
            maxOptions={props.options.length}
            disabled={props.disabled}
            onChange={props.onChange}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The write destinations, nested under their Applied Epic policies. Only suggested
 * policies start open, keeping the ones a run is about above the fold.
 */
function DestinationSection(props: {
  accountId: string | null;
  hasChosenAccount: boolean;
  targets: AmsReconciliationTargetFieldsFragment[];
  loading: boolean;
  syncing: boolean;
  creatingNew: boolean;
  syncedAt: Date | null;
  refreshing: boolean;
  onRefresh?: () => void;
  selected: string[];
  onToggle: (targetId: string) => void;
}) {
  const layout = layOutDestinations(props.targets);
  const hasSuggested = layout.suggestedPolicies.length > 0 || layout.suggestedLoose.length > 0;

  const placeholder = destinationPlaceholder(props);
  const hasAvailable = props.targets.some((target) => target.available);

  const policyRow = (policy: DestinationPolicy) => (
    <PolicyGroupRow
      key={policy.group.id}
      policy={policy}
      creatingNew={props.creatingNew}
      selected={props.selected}
      onToggle={props.onToggle}
    />
  );
  const looseRow = (target: AmsReconciliationTargetFieldsFragment) => (
    <DestinationRow
      key={target.id}
      target={target}
      creatingNew={props.creatingNew}
      checked={props.selected.includes(target.id)}
      onToggle={() => props.onToggle(target.id)}
    />
  );

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
          Write destinations
        </h3>
        {props.onRefresh && !props.creatingNew && (
          <div className="flex items-baseline gap-2">
            {props.syncedAt && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Synced {syncedAgo(props.syncedAt)}
              </span>
            )}
            <button
              type="button"
              onClick={props.onRefresh}
              disabled={props.syncing || props.refreshing}
              className="text-xs font-medium text-sky-700 hover:underline disabled:cursor-default disabled:text-neutral-400 disabled:no-underline dark:text-sky-300 dark:disabled:text-neutral-500"
            >
              Refresh from Epic
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {props.creatingNew
          ? 'Grouped by policy. Everything is created fresh; nothing existing is overwritten.'
          : 'Grouped by policy. Pick one or more; each is reconciled separately.'}
      </p>

      <div className="overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
        {placeholder ?? (
          <>
            {hasSuggested && (
              <>
                <GroupHeader label="Suggested from this document" />
                {layout.suggestedPolicies.map(policyRow)}
                {layout.suggestedLoose.map(looseRow)}
              </>
            )}

            <GroupHeader
              label="Other policies on this account"
              count={layout.otherPolicies.length}
            />
            {layout.otherPolicies.length > 0
              ? layout.otherPolicies.map(policyRow)
              : !props.creatingNew && (
                  <div className="px-3 py-2.5 text-xs text-neutral-500 dark:text-neutral-400">
                    No other policies on this account
                  </div>
                )}
          </>
        )}

        {props.syncing && hasAvailable && (
          <div className="border-t border-neutral-100 px-3 py-2 text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
            Still importing from Applied Epic. More destinations may appear.
          </div>
        )}
      </div>

      {/* Disabled until the Applied Epic create wrappers exist. Kept visible because
          it answers the rows above that the account has no record for. */}
      <Button type="button" color="white" disabled className="self-start">
        Create a new entity
      </Button>
    </section>
  );
}

function PolicyGroupRow(props: {
  policy: DestinationPolicy;
  creatingNew: boolean;
  selected: string[];
  onToggle: (targetId: string) => void;
}) {
  const { group, targets, suggested } = props.policy;
  const [open, setOpen] = React.useState(suggested);
  const panelId = React.useId();

  const selectable = targets.filter((target) => isSelectable(target, props.creatingNew));
  const selectedCount = selectable.filter((target) => props.selected.includes(target.id)).length;

  return (
    <div className="border-b border-neutral-100 last:border-b-0 dark:border-neutral-700/50">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-neutral-50 dark:hover:bg-white/5"
      >
        <ChevronRightIcon
          className={clsx(
            'size-4 shrink-0 text-neutral-400 transition-transform dark:text-neutral-500',
            open && 'rotate-90'
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
            {group.label}
            {props.creatingNew && <Badge color="sky">New</Badge>}
          </div>
          {group.detail && (
            <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
              {group.detail}
            </div>
          )}
        </div>
        <span
          className={clsx(
            'shrink-0 text-xs font-medium',
            selectedCount > 0
              ? 'text-sky-700 dark:text-sky-300'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          {selectedCount > 0
            ? `${selectedCount} of ${selectable.length} selected`
            : `${targets.length} ${targets.length === 1 ? 'destination' : 'destinations'}`}
        </span>
      </button>

      {open && (
        <div id={panelId} className="pb-1">
          {targets.map((target) => (
            <DestinationRow
              key={target.id}
              target={target}
              creatingNew={props.creatingNew}
              nested
              checked={props.selected.includes(target.id)}
              onToggle={() => props.onToggle(target.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The message that replaces the destination list, or null to render the list. A
 * refresh takes over only while no available record is in hand: hiding fetched
 * records behind a minutes-long refresh reads as broken. Unavailable targets don't
 * count, since the refresh may yet turn them up.
 */
function destinationPlaceholder(props: {
  accountId: string | null;
  hasChosenAccount: boolean;
  targets: AmsReconciliationTargetFieldsFragment[];
  loading: boolean;
  syncing: boolean;
  refreshing: boolean;
  creatingNew: boolean;
}): React.ReactNode {
  if (props.creatingNew) {
    return null;
  }
  const refreshing = props.syncing || props.refreshing;
  if (refreshing && !props.targets.some((target) => target.available)) {
    return (
      <div className="flex items-center gap-2 px-3 py-4 text-xs text-neutral-500 dark:text-neutral-400">
        <Spinner color="neutral" />
        Importing this account from Applied Epic. Its policies appear as they arrive.
      </div>
    );
  }
  if (!props.accountId) {
    return (
      <div className="px-3 py-4 text-xs text-neutral-500 dark:text-neutral-400">
        {props.hasChosenAccount
          ? "Couldn't import this account from the AMS. Pick another, or try again."
          : 'Pick an account to see what can be written to it.'}
      </div>
    );
  }
  if (props.loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-4 text-xs text-neutral-500 dark:text-neutral-400">
        <Spinner color="neutral" />
        Loading destinations…
      </div>
    );
  }
  return null;
}

function GroupHeader(props: { label: string; count?: number }) {
  return (
    <div className="flex items-center gap-2 border-y border-neutral-200 bg-neutral-50 px-3 py-1.5 first:border-t-0 dark:border-neutral-700 dark:bg-neutral-900">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
        {props.label}
      </span>
      {props.count != null && (
        <span className="ml-auto text-xs font-medium text-neutral-500 dark:text-neutral-400">
          {props.count}
        </span>
      )}
    </div>
  );
}

function DestinationRow(props: {
  target: AmsReconciliationTargetFieldsFragment;
  /** On an account being created, every row is a record Cara would make. */
  creatingNew?: boolean;
  nested?: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const { target } = props;
  const reconcilable = target.lineOfBusiness != null;
  const detail = !reconcilable
    ? 'Cara cannot reconcile this record type yet'
    : props.creatingNew || target.available
      ? (target.detail ?? '')
      : 'The document states this, but this account has no matching record';

  const selectable = isSelectable(target, props.creatingNew);

  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-3',
        props.nested
          ? 'py-2 pl-9'
          : 'border-b border-neutral-100 py-2.5 last:border-b-0 dark:border-neutral-700/50',
        !selectable && 'opacity-50'
      )}
    >
      {/* One label line tall, so the box centres on the label rather than on the
          label and its detail together. */}
      <span className="flex h-5 flex-none items-center">
        <Checkbox
          color="sky"
          checked={props.checked}
          disabled={!selectable}
          onChange={props.onToggle}
          aria-label={target.label}
        />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
            {target.label}
          </span>
          {!target.available && !props.creatingNew && <Badge color="sky">New</Badge>}
        </div>
        {detail && (
          <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">{detail}</div>
        )}
      </div>
    </div>
  );
}

function syncedAgo(syncedAt: Date, now = new Date()): string {
  const minutes = Math.floor((now.getTime() - syncedAt.getTime()) / 60_000);
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.floor(hours / 24)}d ago`;
}

function isSelectable(target: AmsReconciliationTargetFieldsFragment, creatingNew = false): boolean {
  return target.lineOfBusiness != null && (target.available || creatingNew);
}

/** The code an agent knows the account by, which the server works out. */
function accountCodeOf(match: AmsAccountMatchFieldsFragment): string | null {
  return match.accountCode ?? match.lookupCode ?? null;
}

function accountKey(match: AmsAccountMatchFieldsFragment): string {
  return `${match.integrationId}:${match.externalId}`;
}

function sameAccount(
  left: AmsAccountMatchFieldsFragment | null,
  right: AmsAccountMatchFieldsFragment | null
): boolean {
  return left != null && right != null && accountKey(left) === accountKey(right);
}
